namespace $ {

	export type $shm_hitalama_process_wire_task< Host, Args extends readonly unknown[], Result > =
		( this: Host, ... args: Args )=> Result

	type $shm_hitalama_process_wire_atom_task< Host, Args extends readonly unknown[], Result > =
		$shm_hitalama_process_wire_task< Host, Args, Result > & { orig?: Function }

	type $shm_hitalama_process_wire_inline_entry< Args extends readonly unknown[], Result > = {
		process: $shm_hitalama_process< Result >
		task: ( ... args: Args )=> Result
		run: ( this: $shm_hitalama_process_wire_inline_entry< Args, Result >, ... args: Args )=> Result
		temp: (
			host: $shm_hitalama_process_wire_inline_entry< Args, Result >,
			args: Args,
		)=> $mol_wire_task< $shm_hitalama_process_wire_inline_entry< Args, Result >, Args, Result >
	}

	const $shm_hitalama_process_wire_inline_by_fiber = new WeakMap<
		$mol_wire_task< any, readonly unknown[], unknown >,
		Map< string, $shm_hitalama_process_wire_inline_entry< any, any > >
	>

	const $shm_hitalama_process_wire_inline_current_by_host = new WeakMap<
		object,
		Map< string, $shm_hitalama_process< any > >
	>

	const $shm_hitalama_process_wire_inline_method_by_name = new Map< string, Function >

	export function $shm_hitalama_process_wire_inline_method( name: string ) {
		let method = $shm_hitalama_process_wire_inline_method_by_name.get( name )
		if( method ) return method

		method = function() {}
		Object.defineProperty( method, 'name', { value: name } )
		$shm_hitalama_process_wire_inline_method_by_name.set( name, method )

		return method
	}

	export function $shm_hitalama_process_wire_inline_entry<
		Args extends readonly unknown[],
		Result,
	>(
		process: $shm_hitalama_process< Result >,
		name: string,
		task: ( ... args: Args )=> Result,
	) {
		const run = function(
			this: $shm_hitalama_process_wire_inline_entry< Args, Result >,
			... args: Args
		) {
			const prev = $shm_hitalama_process.current
			$shm_hitalama_process.current = this.process

			try {
				return this.task( ... args )
			} finally {
				$shm_hitalama_process.current = prev
			}
		}
		Object.defineProperty( run, 'name', { value: name } )

		const entry: $shm_hitalama_process_wire_inline_entry< Args, Result > = {
			process,
			task,
			run,
			temp: $mol_wire_task.getter( run ),
		}

		return entry
	}

	export function $shm_hitalama_process_wire_start< Result >(
		process: $shm_hitalama_process< Result >,
	) {
		process.data_update({
			status: 'running',
			started_at: new $mol_time_moment,
			finished_at: undefined,
			promise: undefined,
			result: undefined,
			error: undefined,
		})
	}

	export function $shm_hitalama_process_wire_done< Result >(
		process: $shm_hitalama_process< Result >,
		result: Result,
	) {
		if( process.data_status() === 'superseded' ) return

		process.data_update({
			...( $shm_hitalama_process.result_store_enabled ? { result } : {} ),
			status: 'done',
			finished_at: new $mol_time_moment,
		})
	}

	export function $shm_hitalama_process_wire_fail< Result >(
		process: $shm_hitalama_process< Result >,
		error: unknown,
	) {
		if( process.data_status() === 'superseded' ) return

		process.data_update({
			error,
			status: 'failed',
			finished_at: new $mol_time_moment,
		})
	}

	export function $shm_hitalama_process_wire_wait< Result >(
		process: $shm_hitalama_process< Result >,
		promise: PromiseLike< Result >,
	) {
		if( process.data_status() !== 'running' ) return
		process.data_update({ promise })
	}

	export function $shm_hitalama_process_wire_result< Result >(
		process: $shm_hitalama_process< Result >,
		next: ()=> Result,
	) {
		return $mol_error_fence(
			()=> {
				const result = next()
				$shm_hitalama_process_wire_done( process, result )
				return result
			},
			error => {
				$shm_hitalama_process_wire_fail( process, error )
				return error
			},
			promise => {
				$shm_hitalama_process_wire_wait( process, promise )
				promise.then( ()=> {}, ()=> {} )
				return promise
			},
		)
	}

	export function $shm_hitalama_process_wire_atom_task<
		Host extends object,
		Args extends readonly unknown[],
		Result,
	>(
		host: Host,
		field: PropertyKey,
		descr: TypedPropertyDescriptor< $shm_hitalama_process_wire_task< Host, Args, Result > > | undefined,
		process_by_atom: WeakMap< $mol_wire_atom< Host, Args, Result >, $shm_hitalama_process< Result > >,
		process_ensure?: (
			host: Host,
			args: Args,
			atom: $mol_wire_atom< Host, Args, Result >,
		)=> $shm_hitalama_process< Result >,
	) {
		if( !descr ) descr = Reflect.getOwnPropertyDescriptor( host, field ) as typeof descr
		const orig = descr?.value! ?? ( host as any )[ field ]

		const sup = Reflect.getPrototypeOf( host )!
		if( typeof ( sup as any )[ field ] === 'function' ) {
			Object.defineProperty( orig, 'name', { value: ( sup as any )[ field ].name } )
		}

		const task = function( this: Host, ... task_args: Args ) {
			const atom = $mol_wire_auto() as $mol_wire_atom< Host, Args, Result > | null
			const process_current = atom
				? process_ensure?.( this, task_args, atom ) ?? process_by_atom.get( atom )
				: undefined
			const prev = $shm_hitalama_process.current
			$shm_hitalama_process.current = process_current

			try {
				if( process_current ) {
					return $shm_hitalama_process_wire_result(
						process_current,
						()=> orig.apply( this, task_args as any ),
					)
				}

				return orig.apply( this, task_args as any )
			} finally {
				$shm_hitalama_process.current = prev
			}
		} as $shm_hitalama_process_wire_atom_task< Host, Args, Result >
		Object.defineProperty( task, 'name', { value: orig.name } )
		Object.assign( task, { orig } )

		return { descr, orig, task }
	}

	export function $shm_hitalama_process_wire_atom_process<
		Host extends object,
		Args extends readonly unknown[],
		Result,
	>(
		host: Host,
		method: Function,
		field: PropertyKey,
		args: Args,
		atom: $mol_wire_atom< Host, Args, Result >,
		process_by_atom: WeakMap< $mol_wire_atom< Host, Args, Result >, $shm_hitalama_process< Result > >,
		restart: boolean,
	) {
		let process = process_by_atom.get( atom )

		if( !process || ( restart && process.data_status() !== 'running' ) ) {
			process = new $shm_hitalama_process< Result >(
				async()=> undefined as Result,
				host,
				String( field ),
				args,
			)

			process_by_atom.set( atom, process )
			$shm_hitalama_process_wire_start( process )
			$shm_hitalama_process.register( host, method, process )
			return process
		}

		return process
	}

	export function $shm_hitalama_process_wire_action<
		Host extends object,
		Args extends readonly unknown[],
		Result,
	>(
		host: Host,
		field: PropertyKey,
		descr?: TypedPropertyDescriptor< $shm_hitalama_process_wire_task< Host, Args, Result > >,
	) {
		if( !descr ) descr = Reflect.getOwnPropertyDescriptor( host, field ) as typeof descr
		const orig = descr?.value! ?? ( host as any )[ field ]

		const sup = Reflect.getPrototypeOf( host )!
		if( typeof ( sup as any )[ field ] === 'function' ) {
			Object.defineProperty( orig, 'name', { value: ( sup as any )[ field ].name } )
		}

		const process_by_fiber = new WeakMap<
			$mol_wire_task< Host, Args, Result >,
			$shm_hitalama_process< Result >
		>
		const process_current_by_host = new WeakMap< Host, $shm_hitalama_process< Result > >

		const task = function( this: Host, ... task_args: Args ) {
			const fiber = $mol_wire_auto() as $mol_wire_task< Host, Args, Result > | null
			const process_current = fiber ? process_by_fiber.get( fiber ) : undefined
			const prev = $shm_hitalama_process.current
			$shm_hitalama_process.current = process_current

			try {
				return orig.apply( this, task_args as any )
			} finally {
				$shm_hitalama_process.current = prev
			}
		}
		Object.defineProperty( task, 'name', { value: orig.name } )

		const temp = $mol_wire_task.getter( task )

		const value = function( this: Host, ... args: Args ) {
			const fiber = temp( this, args )
			let process = process_by_fiber.get( fiber )

			if( !process ) {
				process_current_by_host.get( this )?.supersede()

				process = new $shm_hitalama_process< Result >(
					async()=> undefined as Result,
					this,
					String( field ),
					args,
				)

				process_by_fiber.set( fiber, process )
				process_current_by_host.set( this, process )
				$shm_hitalama_process_wire_start( process )
				$shm_hitalama_process.register( this, value, process )
			} else if( process.data_status() !== 'running' ) {
				$shm_hitalama_process_wire_start( process )
			}
			process.data_update({ fiber })

			return $shm_hitalama_process_wire_result( process, ()=> {
				process.run_count_inc()
				return fiber.sync()
			} )
		}

		Object.defineProperty( value, 'name', { value: orig.name + ' ' } )
		Object.assign( value, { orig } )

		const descr2 = { ...descr, value }
		Reflect.defineProperty( host, field, descr2 )

		return descr2
	}

	export function $shm_hitalama_process_wire_inline<
		Host extends object,
		Args extends readonly unknown[],
		Result,
	>(
		host: Host,
		name: string,
		task: ( ... args: Args )=> Result,
		args = [] as unknown as Args,
	): Result {
		const fiber = $mol_wire_auto() as $mol_wire_task< any, readonly unknown[], unknown > | null
		const current = $shm_hitalama_process.current
		let current_by_name = $shm_hitalama_process_wire_inline_current_by_host.get( host )
		if( !current_by_name ) {
			current_by_name = new Map
			$shm_hitalama_process_wire_inline_current_by_host.set( host, current_by_name )
		}

		let entry: $shm_hitalama_process_wire_inline_entry< Args, Result > | undefined
		if( fiber ) {
			let process_by_name = $shm_hitalama_process_wire_inline_by_fiber.get( fiber )
			if( !process_by_name ) {
				process_by_name = new Map
				$shm_hitalama_process_wire_inline_by_fiber.set( fiber, process_by_name )
			}
			entry = process_by_name.get( name )

			if( !entry || ( current && entry.process.data_status() !== 'running' && entry.process.parent !== current ) ) {
				current_by_name.get( name )?.supersede()

				const process = new $shm_hitalama_process< Result >(
					async()=> undefined as Result,
					host,
					name,
					args,
				)
				entry = $shm_hitalama_process_wire_inline_entry( process, name, task )
				process_by_name.set( name, entry )
				$shm_hitalama_process_wire_start( process )
				$shm_hitalama_process.register(
					host,
					$shm_hitalama_process_wire_inline_method( name ),
					process,
				)
				current_by_name.set( name, process )
			}
		} else {
			current_by_name.get( name )?.supersede()

			const process = new $shm_hitalama_process< Result >(
				async()=> undefined as Result,
				host,
				name,
				args,
			)
			entry = $shm_hitalama_process_wire_inline_entry( process, name, task )
			$shm_hitalama_process_wire_start( process )
			$shm_hitalama_process.register(
				host,
				$shm_hitalama_process_wire_inline_method( name ),
				process,
			)
			current_by_name.set( name, process )
		}

		return $shm_hitalama_process_wire_result(
			entry.process,
			()=> entry.temp( entry, args ).sync(),
		)
	}

}
